import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldOptions } from '../../hooks/useFieldOptions';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import EditorFieldOption from './EditorFieldOption';
import EditorOptionsGroupHeading from './EditorOptionsGroupHeading';
import EditorNoResultsMessage from './EditorNoResultsMessage';
import EditorFieldLoadMoreOption from './EditorFieldLoadMoreOption';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import EditorScrollingBodyContainer from './EditorScrollingBodyContainer';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import { PropertySearchSuggestions } from './NLPSuggestions/PropertySearchSuggestions';
import { AutoSizer, List as VirtualizedList, CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import { useNLPIntegration } from '../../hooks/useNLPIntegration';
import { jsx as _jsx } from "react/jsx-runtime";
export const OptionsHeading = styled(EditorOptionsGroupHeading)(({
  theme
}) => ({
  fontSize: theme.typography['heading-100'].fontSize
}));
const EditorConditionFieldOptions = props => {
  const {
    filterFamily,
    hiddenProperties,
    isLoadingFields,
    onSelectField,
    searchString
  } = props;
  const {
    isLoadingFields: isLoadingFieldsInitial,
    props: {
      useOnePanel,
      enableNLPPropertySearchForReporting
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const [loadingMore, setLoadingMore] = useState(false);
  const {
    options: fieldOptions
  } = useFieldOptions(filterFamily, searchString, hiddenProperties);
  const {
    showNLPSuggestions,
    handleOnSelectField,
    suggestedPropertiesResultsRef
  } = useNLPIntegration({
    isEnabled: !!enableNLPPropertySearchForReporting,
    searchString,
    filterFamily,
    onSelectField
  });
  const prevOptionsLength = useRef(fieldOptions.length);
  const prevSearchString = useRef(searchString);
  const virtualizedListRef = useRef(null);
  const listKey = showNLPSuggestions ? 'list-with-nlp' : 'list-without-nlp';
  const cellMeasurerCache = useMemo(() => {
    return new CellMeasurerCache({
      defaultHeight: 40,
      fixedWidth: true
    });
  }, []);
  const remeasureNLPSuggestionsRow = useCallback(() => {
    if (enableNLPPropertySearchForReporting && searchString) {
      var _virtualizedListRef$c;
      cellMeasurerCache.clear(0, 0);
      (_virtualizedListRef$c = virtualizedListRef.current) === null || _virtualizedListRef$c === void 0 || _virtualizedListRef$c.recomputeRowHeights(0);
    }
  }, [enableNLPPropertySearchForReporting, searchString, cellMeasurerCache]);
  const rowCount = (showNLPSuggestions ? 1 : 0) + fieldOptions.length + 1; // +1 for LoadMore

  const rowRenderer = useCallback(({
    index,
    key,
    style,
    parent
  }) => {
    let content = null;
    const nlpSuggestionsOffset = showNLPSuggestions ? 1 : 0;
    if (showNLPSuggestions && index === 0) {
      content = /*#__PURE__*/_jsx(PropertySearchSuggestions, {
        filterFamily: filterFamily,
        searchString: searchString,
        hiddenProperties: hiddenProperties,
        onSelectField: handleOnSelectField,
        suggestedPropertiesResultsRef: suggestedPropertiesResultsRef,
        onLayoutMayHaveChanged: remeasureNLPSuggestionsRow
      });
    } else if (index === rowCount - 1) {
      // Last item is always LoadMore
      content = /*#__PURE__*/_jsx(EditorFieldLoadMoreOption, {
        filterFamily: filterFamily,
        isLoadingMore: loadingMore,
        onLoadMore: setLoadingMore,
        searchString: searchString
      });
    } else {
      const optionIndex = index - nlpSuggestionsOffset;
      if (optionIndex >= 0 && optionIndex < fieldOptions.length) {
        const entry = fieldOptions[optionIndex];
        const spaceStyleProps = optionIndex > 0 || optionIndex === 0 && showNLPSuggestions ? {
          marginBlockStart: '300'
        } : {};
        if (entry.get('heading')) {
          content = /*#__PURE__*/_jsx(OptionsHeading, {
            spaceStyleProps: spaceStyleProps,
            children: /*#__PURE__*/_jsx(TruncateString, {
              children: entry.get('displayName')
            })
          }, `${optionIndex}-${entry.get('heading')}`);
        } else if (entry.get('properties')) {
          content = /*#__PURE__*/_jsx(EditorOptionsGroupHeading, {
            spaceStyleProps: spaceStyleProps,
            children: /*#__PURE__*/_jsx(TruncateString, {
              children: entry.get('displayName')
            })
          }, `${optionIndex}-${entry.get('name')}`);
        } else {
          content = /*#__PURE__*/_jsx(EditorFieldOption, {
            field: entry,
            filterFamily: filterFamily,
            onClick: () => handleOnSelectField(entry)
          }, `${optionIndex}-${entry.get('name')}`);
        }
      }
    }
    if (!content && fieldOptions.length === 0 && !showNLPSuggestions) {
      content = /*#__PURE__*/_jsx(View, {
        padding: "400",
        margin: "auto",
        as: "div",
        children: /*#__PURE__*/_jsx(EditorNoResultsMessage, {
          message: "newCustomerDataFilters.fieldSelectFlow.noFiltersFoundMessage"
        })
      });
    }
    if (!content) {
      return null;
    }
    return /*#__PURE__*/_jsx(CellMeasurer, {
      cache: cellMeasurerCache,
      columnIndex: 0,
      parent: parent,
      rowIndex: index,
      children: ({
        registerChild
      }) =>
      /*#__PURE__*/
      // @bend-expect trellis-tokenized: style is coming from react-virtualized
      _jsx("div", {
        ref: registerChild,
        style: style,
        children: content
      })
    }, key);
  }, [filterFamily, searchString, hiddenProperties, handleOnSelectField, suggestedPropertiesResultsRef, loadingMore, setLoadingMore, fieldOptions, rowCount, remeasureNLPSuggestionsRow, showNLPSuggestions, cellMeasurerCache]);
  useEffect(() => {
    if (prevOptionsLength.current < fieldOptions.length && loadingMore) {
      setLoadingMore(false);
    }
    if (prevOptionsLength.current !== fieldOptions.length || prevSearchString.current !== searchString) {
      var _virtualizedListRef$c2;
      cellMeasurerCache.clearAll();
      (_virtualizedListRef$c2 = virtualizedListRef.current) === null || _virtualizedListRef$c2 === void 0 || _virtualizedListRef$c2.recomputeRowHeights();
    }
    prevOptionsLength.current = fieldOptions.length;
    prevSearchString.current = searchString;
  }, [fieldOptions.length, loadingMore, searchString, cellMeasurerCache]);
  if (useOnePanel && isLoadingFieldsInitial || isLoadingFields) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.fieldSelectFlow.loadingFields"
      }),
      showLabel: true,
      size: "small"
    });
  }
  const hasNoOptionsAtAll = fieldOptions.length === 0 && !showNLPSuggestions;
  if (hasNoOptionsAtAll && !enableNLPPropertySearchForReporting) {
    return /*#__PURE__*/_jsx(EditorScrollingBodyContainer, {
      children: /*#__PURE__*/_jsx(View, {
        padding: "400",
        margin: "auto",
        as: "div",
        children: /*#__PURE__*/_jsx(EditorNoResultsMessage, {
          message: "newCustomerDataFilters.fieldSelectFlow.noFiltersFoundMessage"
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(EditorScrollingBodyContainer, {
    children: /*#__PURE__*/_jsx(AutoSizer, {
      children: ({
        height,
        width
      }) => /*#__PURE__*/_jsx(VirtualizedList, {
        ref: virtualizedListRef,
        deferredMeasurementCache: cellMeasurerCache,
        height: height,
        overscanRowCount: 5,
        rowCount: rowCount,
        rowHeight: cellMeasurerCache.rowHeight,
        rowRenderer: rowRenderer,
        width: width
      }, listKey)
    })
  });
};
export default EditorConditionFieldOptions;